import { useRouter } from 'vue-router';
import { resolveBack, resolveLeave } from '@/utils/backNavigation';

/**
 * The one way to go back.
 *
 * Every back arrow and every "Cancel" that leaves a page uses this: it returns
 * to the screen the user actually came from, and only falls back to the
 * declared parent route when the app was entered straight on this URL.
 *
 * Prefer the `<back-button>` component for the arrow itself; reach for the
 * composable when the same move must happen from code (after a save, a delete,
 * a cancel).
 *
 * Pass `entry` — where a record was entered, see resolveLeave() — to leave the
 * record rather than step back one of its tabs.
 *
 * @return {(fallback?: ?(Object|string), entry?: ?{position: number, hadBack: boolean}) => void}
 */
export function useGoBack() {
    const router = useRouter();

    return function goBack(fallback = null, entry = null) {
        const { action, to, delta } = entry
            ? resolveLeave(window.history.state, entry, fallback)
            : resolveBack(window.history.state, fallback);

        if (action === 'go') {
            router.go(delta);
        } else if (action === 'back') {
            router.back();
        } else if (action === 'replace') {
            router.replace(to);
        }
    };
}
